import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getGetListsQueryOptions, useCreateList } from '@vlabs/api/artemy/client';
import type { CreateListRequest, DepartmentId } from '@vlabs/api/artemy/model';
import { PlusIcon } from '@vlabs/icons';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { useModal } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button/Button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ListFormCreate } from './ListFormCreate';

interface CreateListButtonProps extends Omit<ButtonProps, 'onClick'> {
  departmentId?: DepartmentId;
  onCreateList?: (listId: string) => void;
  enabled?: boolean;
  'data-testid'?: string;
}

const displayName = 'CreateListButton';
const testid = buildTestid(displayName);

function CreateListButton({
  departmentId: initialDepartmentId = '',
  onCreateList = undefined,
  enabled = true,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const createListModal = useModal();
  const queryClient = useQueryClient();
  const createList = useCreateList();

  const onSubmit = async ({ user_data, departmentId }: { user_data: string; departmentId: DepartmentId }) => {
    const data: CreateListRequest = {
      user_data,
      is_active: true,
      owner_department: departmentId,
      is_open: false,
    };

    await createList.mutateAsync({ data }, {
      onSuccess: ({ list_id }) => {
        queryClient.invalidateQueries(getGetListsQueryOptions());
        createListModal.close();
        toast.success(t('lists:подтверждение.создание.успех', { list: user_data }));
        onCreateList?.(list_id);
      },
    });
  };

  return (
    <React.Fragment>
      {createListModal.wrap(
        <ListFormCreate
          initialDepartmentId={initialDepartmentId}
          onCancel={createListModal.close}
          onSubmit={onSubmit}
        />,
      )}
      <Button
        data-testid={dataTestId ? testid(dataTestId)['data-testid'] : 'table.addButton'}
        disabled={!enabled}
        icon={<PlusIcon />}
        onClick={createListModal.open}
        {...props}
      >
        {t('lists:widgets.create list widget.button.create button.label')}
      </Button>
    </React.Fragment>
  );
}

CreateListButton.displayName = displayName;

export { CreateListButton };
